import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form, Alert, Badge, Spinner, InputGroup } from 'react-bootstrap';
import { FaSearch, FaUndo, FaCheckCircle, FaExclamationTriangle, FaPencilAlt } from 'react-icons/fa';

const RestoreDeletedAnswersModal = ({ 
  show, 
  onHide, 
  schoolid,
  teacherid,
  teacherName,
  getDeletedAnswersBySchool,
  restoreAnswer,
  batchRestoreAnswers
}) => {
  const [deletedAnswers, setDeletedAnswers] = useState([]);
  const [filteredAnswers, setFilteredAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [includeRestored, setIncludeRestored] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    if (show && schoolid) {
      loadDeletedAnswers();
    }
  }, [show, schoolid, includeRestored]);

  useEffect(() => {
    filterAnswers();
  }, [deletedAnswers, searchTerm, timeFilter]);

  const loadDeletedAnswers = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await getDeletedAnswersBySchool(schoolid, includeRestored, null, 500, 0);
      setDeletedAnswers(response.data || []);
    } catch (error) {
      setMessage({ type: 'danger', text: `Failed to load deleted answers: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const filterAnswers = () => {
    let filtered = [...deletedAnswers];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(answer => 
        answer.studentName?.toLowerCase().includes(search) ||
        answer.taskName?.toLowerCase().includes(search) ||
        answer.assessmentName?.toLowerCase().includes(search) ||
        answer.id?.toLowerCase().includes(search)
      );
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      
      switch(timeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setDate(now.getDate() - 30);
          break;
      }
      
      filtered = filtered.filter(answer => new Date(answer.date_deleted) >= cutoff);
    }

    setFilteredAnswers(filtered);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const unrestoredIds = filteredAnswers.filter(a => !a.restored).map(a => a.id);
      setSelectedAnswers(new Set(unrestoredIds));
    } else {
      setSelectedAnswers(new Set());
    }
  };

  const handleSelectAnswer = (answerId, isRestored) => {
    if (isRestored) return;
    const newSelected = new Set(selectedAnswers);
    if (newSelected.has(answerId)) {
      newSelected.delete(answerId);
    } else {
      newSelected.add(answerId);
    }
    setSelectedAnswers(newSelected);
  };

  const handleRestore = async () => {
    if (selectedAnswers.size === 0) {
      setMessage({ type: 'warning', text: 'Please select at least one answer to restore' });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRestore = async () => {
    setRestoring(true);
    setMessage({ type: '', text: '' });
    setShowConfirmation(false);

    try {
      const answerIds = Array.from(selectedAnswers);
      
      if (answerIds.length === 1) {
        await restoreAnswer(answerIds[0], teacherid, teacherName);
        setMessage({ type: 'success', text: '✓ Answer restored successfully!' });
      } else {
        const result = await batchRestoreAnswers(schoolid, answerIds, teacherid, teacherName);
        setMessage({ type: 'success', text: `✓ Successfully restored ${result.restoredCount} answer(s)!` });
        
        if (result.errors && result.errors.length > 0) {
          setMessage(prev => ({
            ...prev,
            text: prev.text + ` ${result.errors.length} failed.`
          }));
        }
      }

      setSelectedAnswers(new Set());
      await loadDeletedAnswers();
    } catch (error) {
      setMessage({ type: 'danger', text: `Restoration failed: ${error.message}` });
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaPencilAlt className="me-2" />
            Restore Deleted Answers
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message.text && (
            <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
              {message.text}
            </Alert>
          )}

          <Alert variant="info">
            <small>
              <strong>Note:</strong> Individual answer deletions are typically from data corrections or retakes. 
              Review carefully before restoring to avoid duplicate or incorrect data.
            </small>
          </Alert>

          <div className="mb-3">
            <div className="row g-2">
              <div className="col-md-6">
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search by student, task, or assessment..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
              <div className="col-md-3">
                <Form.Select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Deleted Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Check
                  type="checkbox"
                  label="Show Already Restored"
                  checked={includeRestored}
                  onChange={(e) => setIncludeRestored(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">
              Showing {filteredAnswers.length} of {deletedAnswers.length} deleted answers
              {selectedAnswers.size > 0 && ` | ${selectedAnswers.size} selected`}
            </small>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading deleted answers...</p>
            </div>
          ) : filteredAnswers.length === 0 ? (
            <Alert variant="info">
              <FaCheckCircle className="me-2" />
              No deleted answers found. {includeRestored ? 'Try adjusting your filters.' : 'All clean!'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedAnswers.size > 0 && 
                          selectedAnswers.size === filteredAnswers.filter(a => !a.restored).length}
                      />
                    </th>
                    <th>Student</th>
                    <th>Assessment</th>
                    <th>Task</th>
                    <th>Score</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnswers.map((answer) => (
                    <tr key={answer.id} style={{ 
                      opacity: answer.restored ? 0.6 : 1,
                      backgroundColor: answer.restored ? '#f8f9fa' : 'white'
                    }}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedAnswers.has(answer.id)}
                          onChange={() => handleSelectAnswer(answer.id, answer.restored)}
                          disabled={!!answer.restored}
                        />
                      </td>
                      <td>
                        <strong>{answer.studentName || 'Unknown'}</strong>
                        <br />
                        <small className="text-muted">{answer.studentid}</small>
                      </td>
                      <td>
                        {answer.assessmentName || 'N/A'}
                        <br />
                        <small className="text-muted">{answer.screeningPoint || 'N/A'}</small>
                      </td>
                      <td>{answer.taskName || 'N/A'}</td>
                      <td>
                        {answer.score !== null && answer.score !== undefined ? (
                          <Badge bg={answer.correct ? 'success' : 'danger'}>
                            {answer.score}
                          </Badge>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {answer.deleted_by_teacher_name || 'Unknown'}
                        <br />
                        <small className="text-muted">{answer.deleted_by_teacher_id}</small>
                      </td>
                      <td>
                        {formatDate(answer.date_deleted)}
                        <br />
                        <Badge bg="secondary">{getTimeAgo(answer.date_deleted)}</Badge>
                      </td>
                      <td>
                        {answer.restored ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Restored
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <FaExclamationTriangle className="me-1" />
                            Deleted
                          </Badge>
                        )}
                        {answer.restored && (
                          <div className="mt-1">
                            <small className="text-muted">
                              by {answer.restored_by_teacher_name}
                              <br />
                              {formatDate(answer.date_restored)}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              {selectedAnswers.size > 0 && (
                <span className="text-muted">
                  {selectedAnswers.size} answer(s) selected for restoration
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={onHide} className="me-2">
                Close
              </Button>
              <Button 
                variant="primary" 
                onClick={handleRestore}
                disabled={selectedAnswers.size === 0 || restoring}
              >
                {restoring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Restoring...
                  </>
                ) : (
                  <>
                    <FaUndo className="me-2" />
                    Restore Selected ({selectedAnswers.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmation} onHide={() => setShowConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Restoration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <FaExclamationTriangle className="me-2" />
            <strong>Are you sure?</strong>
          </Alert>
          <p>You are about to restore <strong>{selectedAnswers.size}</strong> deleted answer(s).</p>
          <p className="mb-0">
            <small className="text-muted">
              Restored answers will reappear in assessments and affect student scores and progress.
              Make sure these answers should be restored to avoid data conflicts.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRestore}>
            <FaUndo className="me-2" />
            Yes, Restore Now
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RestoreDeletedAnswersModal;